import { CompanyDistributor } from 'models/companies'
import { isValidElement, useMemo } from 'react'
import useApiService, { tagParent } from 'services/api'
import styled from 'styled-components/macro'
import { Campaign } from '../../models/campaign'
import { Deal } from '../../models/deals'
import { Tag } from '../../models/tags'
import { MULTI_VALUE_SELECT_COLORS } from '../../utils/constants'
import { getStageColorHex } from 'components/SalesStage/stageColors'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { TextPill } from './TextPill'
import { RouterLink } from 'components/UI/Anchor'
import { PillOverflowChip } from 'components/Tables/CommonTableCells/PillOverflowChip'
import { FittedPillRow } from 'components/Tables/CommonTableCells/FittedPillRow'
import { OverflownText } from 'components/OverflownText'
import { useTableLinkVariant } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import { AttributionCode } from '../../features/tradespend/tradespend_api'
import { CompanyGPO } from 'models/companies'

function ConditionalTooltipWrapper({
  shouldWrap,
  tooltipText,
  children,
}: {
  shouldWrap?: boolean
  tooltipText?: string
  children: React.ReactElement
}) {
  const variant = useTableLinkVariant()
  // DS2026 surfaces use the click popover (PillOverflowChip) instead of a
  // tooltip — skip the wrapper so the cell doesn't show both.
  if (shouldWrap && variant !== 'plain') {
    return (
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip>{tooltipText}</Tooltip>}
      >
        {children}
      </OverlayTrigger>
    )
  }
  return <>{children}</>
}

const defaultGrey = MULTI_VALUE_SELECT_COLORS[0].background
const defaultGreyText = MULTI_VALUE_SELECT_COLORS[0].text

export function StagePill({
  stageId,
  className,
}: {
  stageId?: number | null
  className?: string
}) {
  const api = useApiService()
  const { data } = api.useGetSalesStages()
  const { isSettingEnabled } = usePreferences()
  const customOrderEnabled = isSettingEnabled('sales_stage_custom_order')

  if (!stageId) return null
  const stages = data ?? []
  const stage = stages.find((it) => it.id === stageId)
  if (!stage) return null

  // `getStageColorHex` is the single coloring path for stage badges across the
  // app: each pipeline stage's own palette color (stored, or derived by pipeline
  // position), with terminal stages (Won/Lost/Churn) keyed off the stage name.
  const { bg, text } = getStageColorHex(stage, stages, customOrderEnabled)

  return (
    <TextPill
      style={{ marginBottom: 2 }}
      textColor={text}
      backgroundColor={bg}
      className={className}
    >
      {/* Truncates with a styled tooltip only when width-constrained (e.g. the
          flex sales-stage event layout); natural width elsewhere. */}
      <OverflownText tooltipText={stage.stage}>{stage.stage}</OverflownText>
    </TextPill>
  )
}

export function SaleStagesCellFromDeals({
  deals,
  sortAsc,
}: {
  deals?: Deal[] | null
  sortAsc?: boolean
}) {
  if (!deals) return <>-</>

  const stages = deals.reduce((prev, current) => {
    if (!prev.includes(current.sales_stage) && !!current.sales_stage) {
      prev.push(current.sales_stage)
    }
    return prev
  }, [] as number[])

  if (stages.length === 0) return <>-</>

  return <SalesStageCell salesStagesIds={stages} sortAsc={sortAsc} />
}

export function SalesStageCell({
  salesStagesIds,
  sortAsc,
}: {
  salesStagesIds: number[]
  sortAsc?: boolean
}) {
  const api = useApiService()
  const { data } = api.useGetSalesStages()
  const salesStagesToShow = useMemo(() => {
    const stages = data?.filter((it) => salesStagesIds.includes(it.id))
    if (sortAsc === true) {
      return stages?.sort((a, b) => a.stage.localeCompare(b.stage)) || []
    } else if (sortAsc === false) {
      return stages?.sort((a, b) => b.stage.localeCompare(a.stage)) || []
    }
    return stages || []
  }, [data, salesStagesIds])

  const salesStagesNames = useMemo(
    () => salesStagesToShow?.map((it) => it.stage),
    [salesStagesToShow]
  )

  const variant = useTableLinkVariant()

  return (
    <ConditionalTooltipWrapper
      shouldWrap={salesStagesIds.length > 2}
      tooltipText={salesStagesNames?.join(', ')}
    >
      <PillContainer data-testid="sales-stage-pill-container">
        <PillCellBody
          variant={variant}
          pills={salesStagesToShow.map((stage) => (
            <StagePill key={stage.id} stageId={stage.id} />
          ))}
          staticVisibleCount={2}
        />
      </PillContainer>
    </ConditionalTooltipWrapper>
  )
}

export function ContactTagPill({
  contact_tag_id,
}: {
  contact_tag_id?: number
}) {
  const api = useApiService()
  const { data } = api.useGetTags('contacts')
  if (!contact_tag_id) return null
  const contact_tag = data?.find((it) => it.id === contact_tag_id)
  if (!contact_tag) return null
  // Honor a user-picked color; otherwise fall back to the DS2026 default Gray
  // badge (Badges/Gray/Background #f3f4f6, Badges/Gray/Text #374151).
  const palette = contact_tag.color
    ? MULTI_VALUE_SELECT_COLORS.find((c) => c.background === contact_tag.color)
    : null
  return (
    <TextPill
      textColor={palette?.text ?? '#374151'}
      backgroundColor={palette?.background ?? '#F3F4F6'}
    >
      <OverflownText tooltipText={contact_tag.tag}>
        {contact_tag.tag}
      </OverflownText>
    </TextPill>
  )
}

export function ContactTagCell({ deals }: { deals?: Deal[] | null }) {
  const variant = useTableLinkVariant()

  if (!deals) return <>-</>

  const tags = deals.reduce((prev, current) => {
    prev.push(...current.contact_tags)
    return prev
  }, [] as number[])

  if (tags.length === 0) return <>-</>

  return (
    <ConditionalTooltipWrapper shouldWrap={tags.length > 2}>
      <PillContainer>
        <PillCellBody
          variant={variant}
          pills={tags.map((id) => (
            <ContactTagPill key={id} contact_tag_id={id} />
          ))}
          staticVisibleCount={2}
        />
      </PillContainer>
    </ConditionalTooltipWrapper>
  )
}

export function ContactTagCellV2({
  contact_tags,
}: {
  contact_tags?: number[]
}) {
  const api = useApiService()
  const { data: tags } = api.useGetTags('contacts')

  const variant = useTableLinkVariant()

  if (!contact_tags) return <>-</>
  if (contact_tags.length === 0) return <>-</>

  const contactTagsToShow =
    tags?.filter((tag) => contact_tags.includes(tag.id)) ?? []
  const contactTagsNames = contactTagsToShow?.map((tag) => tag.tag)

  return (
    <ConditionalTooltipWrapper
      shouldWrap={contact_tags.length > 2}
      tooltipText={contactTagsNames?.join(', ')}
    >
      <PillContainer>
        <PillCellBody
          variant={variant}
          pills={contact_tags.map((id) => (
            <ContactTagPill key={id} contact_tag_id={id} />
          ))}
          staticVisibleCount={2}
        />
      </PillContainer>
    </ConditionalTooltipWrapper>
  )
}

export function AccountOwnerPill({
  account_owner_id,
}: {
  account_owner_id?: number
}) {
  const api = useApiService()
  const { data } = api.useGetUsers()
  const variant = useTableLinkVariant()
  if (!account_owner_id) return null
  const accOwner = data?.find((it) => it.id === account_owner_id)
  if (!accOwner) return null
  const name = `${accOwner.first_name} ${accOwner.last_name}`
  // On DS2026 surfaces use the standard higher-contrast grey pill (matches
  // GPO/Distributor pills); keep the lighter pill elsewhere.
  return variant === 'plain' ? (
    <TextPill textColor={defaultGreyText} backgroundColor={defaultGrey}>
      <OverflownText tooltipText={name}>{name}</OverflownText>
    </TextPill>
  ) : (
    <TextPill textColor={'#101828'} backgroundColor={'#F9F9FB'}>
      {name}
    </TextPill>
  )
}

export function AccountOwnerCell({
  accountOwnerIds,
  sortAsc,
}: {
  accountOwnerIds: number[]
  sortAsc?: boolean
}) {
  const api = useApiService()
  const { data } = api.useGetUsers()

  const accountOwnersToShow = useMemo(() => {
    if (!accountOwnerIds || accountOwnerIds.length === 0) return []

    const owners =
      data?.filter((user) => accountOwnerIds.includes(user.id)) || []

    if (sortAsc === true) {
      return owners.sort((a, b) =>
        `${a.first_name} ${a.last_name}`.localeCompare(
          `${b.first_name} ${b.last_name}`
        )
      )
    } else if (sortAsc === false) {
      return owners.sort((a, b) =>
        `${b.first_name} ${b.last_name}`.localeCompare(
          `${a.first_name} ${a.last_name}`
        )
      )
    }
    return owners
  }, [data, accountOwnerIds, sortAsc])

  const accountOwnerNames = useMemo(
    () =>
      accountOwnersToShow.map((user) => `${user.first_name} ${user.last_name}`),
    [accountOwnersToShow]
  )

  const variant = useTableLinkVariant()

  if (accountOwnersToShow.length === 0) return <>-</>

  return (
    <ConditionalTooltipWrapper
      shouldWrap={accountOwnersToShow.length > 2}
      tooltipText={accountOwnerNames.join(', ')}
    >
      <PillContainer data-testid="account-owner-pill-container">
        <PillCellBody
          variant={variant}
          pills={accountOwnersToShow.map((user) => (
            <AccountOwnerPill key={user.id} account_owner_id={user.id} />
          ))}
          staticVisibleCount={2}
        />
      </PillContainer>
    </ConditionalTooltipWrapper>
  )
}

/**
 * Shared body for every pill cell. On the DS2026/plain surface it fits as many
 * pills as the (resizable) column width allows and shows a "+N" chip for the
 * rest (see FittedPillRow). Elsewhere it keeps the legacy fixed behaviour:
 * `staticVisibleCount` pills + a "+N" chip.
 *
 * `pills` is the full, ordered list of rendered pill elements (each with a
 * stable key); the overflow chip and the FittedPillRow remount key are derived
 * from those keys so measurement resets when the underlying items change.
 */
function PillCellBody({
  variant,
  pills,
  staticVisibleCount,
}: {
  variant: string
  pills: React.ReactNode[]
  staticVisibleCount: number
}) {
  const renderOverflow = (hidden: React.ReactNode[], count: number) => (
    <PillOverflowChip count={count}>{hidden}</PillOverflowChip>
  )

  if (variant === 'plain') {
    const itemsKey = pills
      .map((pill) => (isValidElement(pill) ? String(pill.key) : ''))
      .join(',')
    return (
      <FittedPillRow
        key={itemsKey}
        pills={pills}
        renderOverflow={renderOverflow}
      />
    )
  }

  const hidden = pills.slice(staticVisibleCount)
  return (
    <>
      {pills.slice(0, staticVisibleCount)}
      {hidden.length > 0 ? renderOverflow(hidden, hidden.length) : null}
    </>
  )
}

export function TaglistCell({
  taglist,
  taglistIds,
  tagType = 'products',
  asc = true,
  maxBadges = 2,
  className,
  nullPlaceholder = <>-</>,
}: {
  taglist?: Tag[] | null
  taglistIds?: number[] | null
  tagType?: tagParent
  asc?: boolean
  maxBadges?: number
  className?: string
  nullPlaceholder?: React.JSX.Element
}) {
  const api = useApiService()
  const { data: tags } = api.useGetTags(tagType)
  const variant = useTableLinkVariant()

  const sortedTaglist = useMemo(() => {
    if (
      (!taglist || taglist.length === 0) &&
      (!taglistIds || taglistIds.length === 0)
    ) {
      return []
    }

    if (taglistIds) {
      const _taglist = tags?.filter((tag) => taglistIds.includes(tag.id)) ?? []
      if (asc) return _taglist?.sort((a, b) => a.tag.localeCompare(b.tag))
      return _taglist?.sort((a, b) => b.tag.localeCompare(a.tag))
    } else if (taglist) {
      if (asc) return taglist.sort((a, b) => a.tag.localeCompare(b.tag))
      return taglist.sort((a, b) => b.tag.localeCompare(a.tag))
    }

    return []
  }, [taglist, taglistIds, tags, asc])

  if (sortedTaglist.length === 0) return nullPlaceholder

  return (
    <ConditionalTooltipWrapper
      shouldWrap={sortedTaglist.length > maxBadges}
      tooltipText={sortedTaglist.map((tag) => tag.tag).join(', ')}
    >
      <PillContainer data-testid="taglist-pill-container" className={className}>
        <PillCellBody
          variant={variant}
          pills={sortedTaglist.map((tag) => renderTagPill(tag))}
          staticVisibleCount={maxBadges}
        />
      </PillContainer>
    </ConditionalTooltipWrapper>
  )
}

export function DistributorsCell({
  distributors,
  distributorsIds,
  asc,
  maxBadges = 2,
  className,
  nullPlaceholder = <>-</>,
}: {
  distributors?: CompanyDistributor[] | null
  distributorsIds?: number[] | null
  asc?: boolean
  maxBadges?: number
  className?: string
  nullPlaceholder?: React.JSX.Element
}) {
  const api = useApiService()
  const { data: distributorsData } = api.useGetDistributors()

  const sortedDistributors = useMemo(() => {
    if (
      (!distributors || distributors.length === 0) &&
      (!distributorsIds || distributorsIds.length === 0)
    ) {
      return []
    }

    if (distributorsIds) {
      const _distributors =
        distributorsData?.filter((distributor) =>
          distributorsIds.includes(distributor.id)
        ) ?? []
      if (asc)
        return _distributors?.sort((a, b) => a.name.localeCompare(b.name))
      return _distributors?.sort((a, b) => b.name.localeCompare(a.name))
    } else if (distributors) {
      return distributors.sort((a, b) => {
        if (asc) return a.name.localeCompare(b.name)
        return b.name.localeCompare(a.name)
      })
    }

    return []
  }, [distributors, asc, distributorsData])

  const variant = useTableLinkVariant()

  if (sortedDistributors.length === 0) return nullPlaceholder

  return (
    <ConditionalTooltipWrapper
      shouldWrap={sortedDistributors.length > maxBadges}
      tooltipText={sortedDistributors
        .map((distributor) => distributor.name)
        .join(', ')}
    >
      <PillContainer
        data-testid="distributors-pill-container"
        className={className}
      >
        <PillCellBody
          variant={variant}
          pills={sortedDistributors.map((distributor) =>
            renderDistributorPill(distributor)
          )}
          staticVisibleCount={maxBadges}
        />
      </PillContainer>
    </ConditionalTooltipWrapper>
  )
}

function renderDistributorPill(distributor: CompanyDistributor) {
  return (
    <TextPill
      key={distributor.id}
      textColor={defaultGreyText}
      backgroundColor={defaultGrey}
    >
      <OverflownText tooltipText={distributor.name}>
        {distributor.name}
      </OverflownText>
    </TextPill>
  )
}

function renderTagPill(tag: Tag) {
  // Honor a user-picked color; otherwise fall back to the DS2026 default Gray
  // badge (Badges/Gray/Background #f3f4f6, Badges/Gray/Text #374151) — matches
  // renderContactTagPill so untagged tags don't all share one palette color.
  const palette = tag.color
    ? MULTI_VALUE_SELECT_COLORS.find((c) => c.background === tag.color)
    : null
  return (
    <TextPill
      key={tag.id}
      textColor={palette?.text ?? '#374151'}
      backgroundColor={palette?.background ?? '#F3F4F6'}
    >
      {/* Styled tooltip with the full tag when the chip truncates to "…". */}
      <OverflownText tooltipText={tag.tag}>{tag.tag}</OverflownText>
    </TextPill>
  )
}

function renderCampaignPill(it: {
  value: number | string
  label: string
  color?: string | null
}) {
  // Honor a user-picked color; otherwise fall back to the DS2026 default Gray
  // badge — matches renderContactTagPill so campaigns without a chosen color
  // no longer all render as lime green (#64d07d).
  const palette = it.color
    ? MULTI_VALUE_SELECT_COLORS.find((c) => c.background === it.color)
    : null
  const textColor = palette?.text ?? '#374151'
  const backgroundColor = palette?.background ?? '#F3F4F6'
  return (
    <TextPill
      key={it.value}
      textColor={textColor}
      backgroundColor={backgroundColor}
      style={{ cursor: 'pointer', maxWidth: '100%' }}
    >
      <RouterLink
        to={`/campaigns/${it.value}`}
        style={{ color: textColor, cursor: 'pointer' }}
        className="block min-w-0 max-w-full"
      >
        <OverflownText tooltipText={it.label} placement="bottom">
          {it.label}
        </OverflownText>
      </RouterLink>
    </TextPill>
  )
}

export function CampaignsCell({
  campaigns,
  campaignsIds,
  asc,
}: {
  campaigns?: Campaign[] | null
  campaignsIds?: number[] | null
  asc?: boolean
}) {
  const api = useApiService()
  const { data: campaignsData } = api.useGetCampaignsOptions()

  const sorted = useMemo(() => {
    if (
      (!campaigns || campaigns.length === 0) &&
      (!campaignsIds || campaignsIds.length === 0)
    )
      return []

    if (campaignsIds) {
      if (!campaignsData) return []

      const _campaigns =
        campaignsData?.filter((co) =>
          campaignsIds.map((id) => Number(id)).includes(Number(co.value))
        ) ?? []

      if (asc) return _campaigns?.sort((a, b) => a.label.localeCompare(b.label))
      return _campaigns?.sort((a, b) => b.label.localeCompare(a.label))
    }

    if (campaigns) {
      return (
        campaigns
          .filter((campaign) => campaign != null)
          .sort((a, b) => {
            if (asc) return a.name.localeCompare(b.name)
            return b.name.localeCompare(a.name)
          })
          .map((campaign) => ({
            value: campaign.id,
            label: campaign.name,
            color: campaign.color,
          })) ?? []
      )
    }

    return []
  }, [campaigns, asc, campaignsData])

  const variant = useTableLinkVariant()

  return (
    <ConditionalTooltipWrapper
      shouldWrap={sorted.length > 2}
      tooltipText={sorted.map((campaign) => campaign.label).join(', ')}
    >
      <PillContainer>
        <PillCellBody
          variant={variant}
          pills={sorted.map((it) => renderCampaignPill(it))}
          staticVisibleCount={2}
        />
      </PillContainer>
    </ConditionalTooltipWrapper>
  )
}

export function AttributionCodeCell({
  attributionCodes,
  nullPlaceholder = <span className="text-gray-400">-</span>,
  limit = 1,
  asc = false,
  onCodeClick,
}: {
  attributionCodes?: AttributionCode[] | null
  nullPlaceholder?: React.ReactNode
  limit?: number
  asc?: boolean
  onCodeClick?: (code: AttributionCode) => void
}) {
  const sortedCodes = useMemo(() => {
    if (!attributionCodes || attributionCodes.length === 0) return []
    return attributionCodes
      .slice(0, limit)
      .sort((a, b) =>
        asc ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name)
      )
  }, [attributionCodes, asc, limit])

  if (!attributionCodes || attributionCodes.length === 0) return nullPlaceholder

  return (
    <div className="flex flex-wrap gap-1">
      {sortedCodes.map((code) => {
        const color = code.color
          ? MULTI_VALUE_SELECT_COLORS.find((c) => c.background === code.color)
          : MULTI_VALUE_SELECT_COLORS[0]

        return (
          <TextPill
            key={code.id}
            textColor={color?.text ?? '#ffffff'}
            backgroundColor={color?.background ?? '#64d07d'}
            style={{ cursor: onCodeClick ? 'pointer' : 'default' }}
            onClick={() => onCodeClick && onCodeClick(code)}
            title={code.description || code.name}
          >
            <span>{code.name}</span>
            {code.is_default && (
              <span className="ml-1 text-xs opacity-75">(Default)</span>
            )}
          </TextPill>
        )
      })}
    </div>
  )
}

export function GPOsCell({
  gpos,
  gposIds,
  asc,
  maxBadges = 2,
  className,
  nullPlaceholder = <>-</>,
}: {
  gpos?: CompanyGPO[] | null
  gposIds?: number[] | null
  asc?: boolean
  maxBadges?: number
  className?: string
  nullPlaceholder?: React.JSX.Element
}) {
  const api = useApiService()
  const { data: gposData } = api.useGetGPOs()

  const sortedGpos = useMemo(() => {
    if ((!gpos || gpos.length === 0) && (!gposIds || gposIds.length === 0)) {
      return []
    }

    if (gposIds) {
      const _gpos = gposData?.filter((gpo) => gposIds.includes(gpo.id)) ?? []
      if (asc) return _gpos?.sort((a, b) => a.name.localeCompare(b.name))
      return _gpos?.sort((a, b) => b.name.localeCompare(a.name))
    } else if (gpos) {
      return gpos.sort((a, b) => {
        if (asc) return a.name.localeCompare(b.name)
        return b.name.localeCompare(a.name)
      })
    }

    return []
  }, [gpos, asc, gposData])

  const variant = useTableLinkVariant()

  if (sortedGpos.length === 0) return nullPlaceholder

  return (
    <ConditionalTooltipWrapper
      shouldWrap={sortedGpos.length > maxBadges}
      tooltipText={sortedGpos.map((gpo) => gpo.name).join(', ')}
    >
      <PillContainer data-testid="gpos-pill-container" className={className}>
        <PillCellBody
          variant={variant}
          pills={sortedGpos.map((gpo) => renderGpoPill(gpo))}
          staticVisibleCount={maxBadges}
        />
      </PillContainer>
    </ConditionalTooltipWrapper>
  )
}

function renderGpoPill(gpo: CompanyGPO) {
  return (
    <TextPill
      key={gpo.id}
      textColor={defaultGreyText}
      backgroundColor={defaultGrey}
    >
      <OverflownText tooltipText={gpo.name}>{gpo.name}</OverflownText>
    </TextPill>
  )
}

export const PillContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  width: 100%;
  padding: 4px;
`

// Export TagCountBadge for convenience
export { TagCountBadge } from './TagCountBadge'
